/**
 * Open the cover drafted on the board as real flex/OT shifts.
 *
 * Two steps on purpose: Shifts/Create makes them as drafts, Shifts/Publish is
 * what actually offers them to workers. Splitting it means a failure to create
 * never leaves half a batch published, and the publish call is a single
 * deliberate act rather than a side effect of drafting.
 *
 * NOT via FTShifts/CreateFlexShift: that endpoint requires each request to
 * carry an FtShiftId pointing at a real dbo.FTShift row, and this location has
 * none — every request would be silently skipped and the call would report
 * success having created nothing.
 */
import { useCallback, useMemo, useState } from "react";

import shiftsApi from "../../../../services/shifts-api";
import { apiErrorMessage } from "../../../../utils/api-error";
import { FlexRequestRow } from "../load-balance-board/types";
import { PlantWorkcenter } from "./use-roster-data";

export type SnackbarSeverity = "success" | "error" | "warning" | "info";

export interface SnackbarState {
	open: boolean;
	message: string;
	severity: SnackbarSeverity;
}

const CLOSED: SnackbarState = { open: false, message: "", severity: "info" };

export interface UsePublishDraftsArgs {
	workcenters: PlantWorkcenter[];
	currentUserId: string | null | undefined;
	/** Reload the week once the shifts are out. */
	onPublished: () => void | Promise<void>;
}

export function usePublishDrafts({ workcenters, currentUserId, onPublished }: UsePublishDraftsArgs) {
	const [snackbar, setSnackbar] = useState<SnackbarState>(CLOSED);
	const notify = useCallback((message: string, severity: SnackbarSeverity) => {
		setSnackbar({ open: true, message, severity });
	}, []);
	const closeSnackbar = useCallback(() => setSnackbar((prev) => ({ ...prev, open: false })), []);

	const publishDrafts = useCallback(
		async (requests: FlexRequestRow[]) => {
			if (!requests.length) {
				return;
			}
			const workcenterIdByName = new Map(workcenters.map((wc) => [wc.name, wc.id]));

			// Refuse rather than publish something half-formed: a shift with no work
			// centre or no role is not something a worker can meaningfully accept.
			const unresolved = requests.filter((request) => !workcenterIdByName.get(request.workcenter) || !request.jobRoleId);
			if (unresolved.length) {
				notify(
					`Cannot publish: ${unresolved.length} request${unresolved.length === 1 ? "" : "s"} could not be matched to a work center and job role.`,
					"error"
				);
				return;
			}

			try {
				const created = await shiftsApi.createShifts(
					requests.map((request) => ({
						startDateTime: request.startIso,
						endDateTime: request.endIso,
						jobId: request.jobRoleId,
						workCenterId: workcenterIdByName.get(request.workcenter),
						numberOfWorkersNeeded: request.heads,
						shiftPremium: request.method === "ot" ? "1.5" : undefined,
						shiftNote: `Cover for ${request.workcenter} · ${request.jobRole} · ${request.window}`,
						createdBy: Number(currentUserId),
						isPublished: false,
						// Must be present even when empty — the server iterates it unguarded.
						shiftSkills: [],
					}))
				);

				const shiftIds = (created || []).map((shift) => shift.id).filter(Boolean);
				if (!shiftIds.length) {
					notify("The shifts could not be created — nothing was published.", "error");
					return;
				}

				await shiftsApi.publishShifts(shiftIds);
				notify(`${shiftIds.length} shift${shiftIds.length === 1 ? "" : "s"} published to the flex pool.`, "success");
				void onPublished();
			} catch (error) {
				console.error("Failed to publish flex/OT requests:", error);
				notify(apiErrorMessage(error, "Could not publish the requests. Nothing was sent."), "error");
				// Rethrow so the board does not flip to "Published" on a failure.
				throw error;
			}
		},
		[workcenters, currentUserId, onPublished, notify]
	);

	return useMemo(() => ({ snackbar, closeSnackbar, publishDrafts }), [snackbar, closeSnackbar, publishDrafts]);
}

export type PublishDrafts = ReturnType<typeof usePublishDrafts>;
